import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CreateTeacherData, TeacherProfileValues, UpdateTeacherData, teachersApi } from '@/services';

export const adminTeachersKeys = {
  all: ['admin', 'teachers'] as const,
  list: ['admin', 'teachers', 'list'] as const,
  detail: (id: string) => ['admin', 'teachers', 'detail', id] as const,
  categories: ['admin', 'teachers', 'categories'] as const,
  pending: ['admin', 'teachers', 'pending'] as const,
};

export function useAdminTeachersListQuery() {
  return useQuery({
    queryKey: adminTeachersKeys.list,
    queryFn: () => teachersApi.list(),
  });
}

export function useAdminTeacherQuery(id?: string) {
  return useQuery({
    queryKey: id ? adminTeachersKeys.detail(id) : adminTeachersKeys.detail(''),
    queryFn: () => teachersApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateTeacherMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTeacherData) => teachersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}

export function useUpdateTeacherMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeacherData }) => teachersApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.detail(variables.id) });
    },
  });
}

export function useDeleteTeacherMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teachersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}

export function useToggleTeacherPublishedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teachersApi.togglePublished(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}

export function useReorderTeachersMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => teachersApi.reorder(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}

export function useAdminTeacherCategoriesQuery() {
  return useQuery({
    queryKey: adminTeachersKeys.categories,
    queryFn: () => teachersApi.listCategories(),
  });
}

export function useCreateTeacherCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; color?: string; sortOrder?: number }) => teachersApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}

export function useUpdateTeacherCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; color?: string; sortOrder?: number }) =>
      teachersApi.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}

export function useDeleteTeacherCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teachersApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}

export function usePendingTeacherSubmissionsQuery(enabled = true) {
  return useQuery({
    queryKey: adminTeachersKeys.pending,
    queryFn: () => teachersApi.listPending(),
    enabled,
  });
}

export function useApproveTeacherSubmissionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: TeacherProfileValues }) => teachersApi.approve(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}

export function useRejectTeacherSubmissionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => teachersApi.reject(id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminTeachersKeys.all });
    },
  });
}
